import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { getRoutesDirections } from '@/data/libraries/Routes';

import { Link, useNavigate } from 'react-router-dom';
import type { NavigateFunction } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';
import { changeLoginState, changeRegisterState, setFromBuyButton } from '@/ui/store/slices/showModalsSlice';

import { useScreenSize } from '@/ui/hooks/useScreenSize';
import type { ScreenSize } from '@/ui/hooks/useScreenSize';

import { Container, Row, Col, Button, Form, Modal } from 'react-bootstrap';

import '@/ui/components/forms/form.css';

import UserController from '@/controllers/UserController';

import { getUrlFromCssVariable } from '@/domain/utils/CssToImage';
import { translate } from '@/domain/utils/translate';

import { IRegistrationData } from '@/domain/interfaces/IUser';
import { IRegisterResponse } from '@/domain/interfaces/IRegisterResponse';
import { IStore } from '@/domain/interfaces/IStore';

import CloseIcon from '@/ui/assets/CloseIcon';

import SignOutForm from '@/ui/components/user/SignOutForm';
import ContentModal from '@/ui/components/modals/ContentModal';
import TwoButtonsGroup from '@/ui/components/TwoButtonsGroup';
import HeaderMobileUser from '@/ui/components/user/HeaderMobileUser';
import UserDataForm from '@/ui/components/user/UserDataForm';
import PDFViewer from '@/ui/components/PDFViewer';


interface PropsSingUpForm {
  setShowRegisterAlert?: (show: boolean) => void;
}

/** Componente formulario para registro email y contraseña
 *@component
 */
const SignUpForm = ({
  setShowRegisterAlert
}: PropsWithChildren<PropsSingUpForm>): JSX.Element => {

  /** Actualización de estado Redux */
  const dispatch = useAppDispatch();

  /** URL del logo de modales */
  const LOGO = getUrlFromCssVariable('--img-logo-modals-white');

  /** Hook para generar una navegación dentro de la aplicación.  */
  const navigate: NavigateFunction = useNavigate();

  /** Estado Redux Tienda */
  const storeData = useAppSelector((state: RootState) => state.storeData) as IStore;

  /** Estado Redux show modal */
  const fromBuyButton = useAppSelector((state) => state.showModals.fromBuyButton);

  /** Rutas URLs */
  const routesDirections = getRoutesDirections();

  /** Hook que contiene las dimensiones de la pantalla y maneja los componentes de acuerdo al resultado obtenido */
  const { width, maxLargeWidth }: ScreenSize = useScreenSize();
  /** Variable que define si es entorno mobile o desktop */
  const isMobile: boolean = width < maxLargeWidth;

  /** Evento que muestra y oculta el modal para login*/
  const handleShowLogin = (): void => {
    dispatch(changeLoginState());
  }
  /** Evento que cierra el modal de registro */
  const closeRegister = (): void => {
    handleShowRegister();
  };

  /** Estado inicial del modal para Registro */
  const showRegister: boolean = useAppSelector((state) => state.showModals.showRegister);
  /** Evento que muestra y oculta el modal para registro*/
  const handleShowRegister = (): void => {
    dispatch(changeRegisterState());
  }

  /** Referencia al check terminos y condiciones
   * @constant {HTMLInputElement}
   */
  const termsCheckRef = useRef<HTMLInputElement>(null);

  /** Referencia al check politica de privacidad
   * @constant {HTMLInputElement}
   */
  const privacyCheckRef = useRef<HTMLInputElement>(null);

  /** Referencia al check mantenerme conectado
   * @constant {HTMLInputElement}
   */
  const sessionCheckRef = useRef<HTMLInputElement>(null);

  const emptyUserData: IRegistrationData = {
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: { number: '', country: '', },
    identificationId: { type: '', value: '', },
    idType: '',
    identification: '',
    error: true,
  };

  /**Estado de valores del input para documento */
  const userData = useRef<IRegistrationData>(emptyUserData);

  /** Maneja un state para ocultar la ventana de cierre de modal al cargar la web    */
  const [closeModal, setCloseModal] = useState<boolean>(false);
  /** Funciones que Muestra / Oculta el modulo de confirmación cierre de modal*/
  const switchCloseModal = (): void => setCloseModal(!closeModal);

  /** Estado para controlar si el botón está deshabilitado */
  const [validData, setValidData] = useState<boolean>(false);

  /** Estado para controlar si se muestra el modal de términos y condiciones */
  const [termsAndConditionFlag, setTermsAndConditionFlag] = useState<boolean>(false);
  /** Función para mostrar el modal de términos y condiciones */
  const handleShowTermsAndConditions = (): void => setTermsAndConditionFlag(!termsAndConditionFlag);

  /** Estado para controlar si se muestra el modal de politica de privacidad */
  const [policyPrivacyFlag, setPolicyPrivacyFlag] = useState<boolean>(false);
  /** Función para mostrar el modal de términos y condiciones */
  const handleShowPolicyPrivacy = (): void => setPolicyPrivacyFlag(!policyPrivacyFlag);

  /** Almacena la funcion del controlador para hacer un registro
   * @constant {object}
   */
  const { clientRegistration } = UserController();

  /** Resetea y Cierra el Formulario    */
  const closeForm = (): void => {
    resetForm();
    dispatch(setFromBuyButton(false));
    handleShowRegister();
  };

  /** Cierra el formulario de registro */
  const goToLoginForm = (): void => {
    setEmailAlertModal(false);
    handleShowRegister();
    handleShowLogin();
  };

  /** Resetea el formulario de Registro,
   * Enciende el Input email y oculta el resto de Inputs,
   * Define el Input en su estado inicial,
   * Oculta iconos de error y componentes que no corresponden al input de email,
   * Borra todos los valores del form
   */
  const resetForm = (): void => {
    setCloseModal(false);
    //signUpFormRef.current?.reset();
  };


  /** Actualiza data de usuario */
  const updateClientData = (clientDelails: IRegistrationData): void => {
    userData.current = { ...clientDelails };
    userData.current.idType = clientDelails.identificationId.type;
    userData.current.identification = clientDelails.identificationId.value;
    handleCheckOptions();
  };

  /** Validación los check de las opciones obligatorias. */
  const handleCheckOptions = (): void => {
    const termsCheck: boolean = termsCheckRef.current?.checked ?? false;
    const privacyCheck: boolean = privacyCheckRef.current?.checked ?? false;
    if (termsCheck && !userData.current.error && privacyCheck) {
      setValidData(true);
    } else {
      setValidData(false);
    }
  }


  /** Evento submit del formulario de login  */
  const continueToSignUp = async (): Promise<void> => {
    setValidData(false);

    const keepLogin: boolean = sessionCheckRef.current?.checked ?? true;

    /** Registra desde el controlador */
    const doSingUpV: IRegisterResponse = await clientRegistration(userData.current, false, keepLogin);
    if (doSingUpV.errors.length > 0) {
      console.error('BAD', doSingUpV);
    } else {
      // Solo navegar al carrito si fromBuyButton es true
      if (fromBuyButton) {
        navigate(routesDirections.CART_ROUTE);
      }

      // Restablece el estado fromBuyButton
      dispatch(setFromBuyButton(false));

      if (setShowRegisterAlert) {
        setShowRegisterAlert(true);
      }

      closeForm();
    }
  };

  /**  Estado que define si se muestra el modal de email que ya existe 	*/
  const [emailAlertModal, setEmailAlertModal] = useState(false);

  /**  Evento que muestra y oculta el modal de email existente	*/
  const handleShowEmailAlertModal = (): void => {
    if (userData.current) {
      userData.current.email = '';
    }
    setEmailAlertModal(!emailAlertModal);
  };

  const EMAIL_EMAIL_ALERT_TITLE_TEXT: string = 'Correo registrado';
  const EMAIL_EMAIL_ALERT_DESCRIPTION_TEXT: string = `El correo ${userData.current?.email} ya está registrado. Por favor, inicia sesión o ingresa un correo electrónico diferente.`;

  /**  Contenido del modal de quienes somos 	*/
  const emailAlertModalProp: JSX.Element = (
    <>
      <Col lg={12} className='text-center'>
        <p className='text-secondary-2 text-justify p-large mt-4'>{EMAIL_EMAIL_ALERT_DESCRIPTION_TEXT}</p>
      </Col>
      <TwoButtonsGroup
        classContainer='pt-3 pb-1 px-0'
        firstButtonClass='btn-secondary-icon-small  '
        firstButtonText={'INGRESAR OTRO CORREO'}
        firstButtonClick={handleShowEmailAlertModal}
        secondButtonClass={'btn-primary-icon-small'}
        secondButtonText={'INICIAR SESIÓN'}
        secondButtonClick={goToLoginForm}
      />
    </>
  );


  useEffect((): (() => void) => {

    // Limpia el estado fromBuyButton cuando el componente se desmonte
    return () => {

      dispatch(setFromBuyButton(false));
    };
  }, [dispatch]);


  /**CONSTANTES DE TEXTO */
  const ASK_EXIT_TEXT = translate('session.ask_exit_text');
  const TERM_CONDITIONS_TEXT = translate('session.term_conditions_text');
  const TERM_CONDITIONS_COMPLEMENT_LINK = translate('session.term_conditions_complement_link');
  const PRIVACY_POLICY_TEXT = translate('session.privacy_policy_text');
  const PRIVACY_POLICY_COMPLEMENT_LINK = translate('session.privacy_policy_complement_link');
  const SING_UP_LABEL = translate('session.sing_up_label');
  const SING_UP_TEXT = translate('session.sing_up_text');
  const REGISTER_LABEL = translate('session.register_label');

  return (
    <>
      <Modal
        id="sign-up-modal"
        show={showRegister}
        onHide={handleShowRegister}
        fullscreen={true}
        animation={false}
        scrollable
        dialogClassName='m-0 g-0 p-0 '
        contentClassName='rounded-0 d-flex flex-column justify-content-between shadow border-0 '>
        {isMobile && (
          <Modal.Header className=' d-flex p-0  bg-ilis-modal '>
            <HeaderMobileUser
              handleIconClick={closeRegister}
              logoSrc={LOGO}
              iconClass='btn-icon-dark  svg-primary-4'
              logoClass='logo-header-modal'
              iconPosition='right'
            />
          </Modal.Header>
        )}

        <Modal.Body className='g-0 p-0 m-0 '>
          <Container fluid>
            <Row className={'min-vh-50 d-flex align-items-end flex-column p-0 bg-ilis-modal'}>
              {!isMobile && (
                <Col className='align-self-center d-flex justify-content-around pb-5'>


                  <div className='text-center w-100 py-3'>
                    <img src={LOGO} alt='logo' className='d-sm-none logo-full-modal'></img>
                  </div>

                  <div>
                    <img
                      src={LOGO}
                      alt='logo'
                      className='login-desktop-logo d-none d-sm-block position-absolute top-10 start-10 logo-full-modal'></img>
                  </div>
                  <Col lg={12} xs={12} className='d-flex justify-content-end pe-5'>
                    <CloseIcon onClick={closeRegister} classMain='btn-icon-dark my-5 svg-primary-4 ' />
                  </Col>
                </Col>
              )}

              <Col
                xs={12}
                sm={6}
                lg={5}
                className={
                  isMobile ? `bg-primary-4  ` : `bg-primary-4 position-absolute top-50 start-50 translate-middle rounded-3  ${!isMobile && 'shadow'}`
                }>
                <Row>
                  <Col className='d-flex justify-content-center '>
                    <Col lg={11} className={isMobile ? '' : 'py-4 px-5 min-vh-25 align-items-center flex-column '}>
                      {closeModal && (
                        <Row>
                          <Col className='pt-5'>
                            <SignOutForm message={ASK_EXIT_TEXT} submit={closeForm} cancel={switchCloseModal} />
                          </Col>
                        </Row>
                      )}
                      <Row>
                        <Col className={closeModal ? 'd-none' : 'display'}>
                          <Row>
                            <Col className={isMobile ? 'mt-3 text-center' : 'pb-5 pt-5 text-center'}>
                              <span className={'h2 fw-bold text-secondary-1'}>{SING_UP_LABEL}</span>
                              <div className='pt-3 text-gray'>
                                <p className={isMobile ? 'p-regular ' : 'text-center mb-0'}>{SING_UP_TEXT}</p>
                              </div>
                            </Col>
                          </Row>
                          <UserDataForm
                            clientData={userData.current}
                            updateClientData={updateClientData}
                            isRegister={true}
                            handleShowEmailAlertModal={handleShowEmailAlertModal}
                          />
                          <Row className='mb-3 mx-auto' style={{ maxWidth: '600px', width: '70%' }}>
                            <Col className='md-auto'>
                              <Form.Group className='d-flex align-items-center justify-content-left'>
                                <Form.Check.Input
                                  type='checkbox'
                                  ref={termsCheckRef}
                                  onChange={handleCheckOptions}
                                  className='me-3'
                                />
                                <Form.Check.Label>
                                  <span className='p-large fw-bold'>{TERM_CONDITIONS_TEXT} </span>
                                  <Link to={'#'} className='text-tertiary-4 p-large fw-bold' onClick={handleShowTermsAndConditions}>
                                    {TERM_CONDITIONS_COMPLEMENT_LINK}
                                  </Link>
                                </Form.Check.Label>
                              </Form.Group>
                              <Form.Group className='d-flex align-items-center justify-content-left'>
                                <Form.Check.Input
                                  type='checkbox'
                                  ref={privacyCheckRef}
                                  onChange={handleCheckOptions}
                                  className='me-3'
                                />
                                <Form.Check.Label>
                                  <span className='p-large fw-bold'>{PRIVACY_POLICY_TEXT} </span>
                                  <Link to={'#'} className='text-tertiary-4 p-large fw-bold' onClick={handleShowPolicyPrivacy}>
                                    {PRIVACY_POLICY_COMPLEMENT_LINK}
                                  </Link>
                                </Form.Check.Label>
                              </Form.Group>
                            </Col>
                          </Row>

                          <Row className={isMobile ? 'd-grid gap-2 pb-5 mb-4 px-4 ' : 'd-grid gap-2 pb-5'}>
                            <Button
                              className={`btn-primary-text-standard text-primary-4 ${!validData ? 'disabled' : ''}`}
                              onClick={continueToSignUp}
                              disabled={!validData}>
                              <span className='p-large'>{REGISTER_LABEL}</span>
                            </Button>
                          </Row>

                        </Col>
                      </Row>
                    </Col>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
          <ContentModal
            dialogClassName='modal-dialog-centered'
            contentClassName='rounded-4 mh-400-px d-flex justify-content-between shadow border-0'
            modalClassName='bg-transparent-black'
            showModal={emailAlertModal}
            contentColClass={9}
            onClose={handleShowEmailAlertModal}
            modalSize='lg'
            message={EMAIL_EMAIL_ALERT_TITLE_TEXT}
            classMessage='text-secondary-1 h1 text-center mt-4'
            content={emailAlertModalProp}
          />
        </Modal.Body>
      </Modal>

      {termsAndConditionFlag && (<Modal
        className='bg-transparent-black'
        show={termsAndConditionFlag}
        size='xl'
        scrollable
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded-4 d-flex flex-column justify-content-between shadow border-0'
        onHide={handleShowTermsAndConditions}>
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body className={isMobile ? 'h-460-px' : ''} style={{ height: '90vh' }}>
          <PDFViewer url={storeData.termsConditionsUrl ?? ''} />
        </Modal.Body>
      </Modal>
      )}

      {policyPrivacyFlag && (<Modal
        className='bg-transparent-black'
        show={policyPrivacyFlag}
        size='xl'
        scrollable
        dialogClassName='modal-dialog-centered'
        contentClassName='rounded-4 d-flex flex-column justify-content-between shadow border-0'
        onHide={handleShowPolicyPrivacy}>
        <Modal.Header closeButton>
        </Modal.Header>
        <Modal.Body className={isMobile ? 'h-460-px' : ''} style={{ height: '90vh' }}>
          <PDFViewer url={storeData.privacyPolicyUrl ?? ''} />
        </Modal.Body>
      </Modal>
      )}
    </>
  );
};

export default SignUpForm;
